import { AttributeBox, ButtonGeneral, SegmentedControl, typographyRem } from '@by-miles/bymiles-styles'
import { DataLayer } from '@by-miles/data-layer'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import React, { useContext, useEffect, useState } from 'react'

import { useQuote } from '../../../../hooks/useQuote'
import { useDriverPageContext } from '../../../../pages/DriverPageProvider'
import { usePreQuoteContext } from '../../../../pages/PageValidator'
import { ProviderContext } from '../../../../Provider'
import { omitObjectKey } from '../../../../utils/omitObjectKey'
import { AccessibleFormWrapper } from '../../../AccessibleFormWrapper'
import { ToolTip } from '../../../ToolTip'
import { GenericSectionStyle } from '../../GenericSectionStyle'
import { YourTheir, YouThey, YouveTheyve } from '../../utils/driverCopyChange'
import { AddConvictionsModal } from './AddConvictionsModal'
const { h4: H4, h5: H5, paragraphBody: ParagraphBody } = typographyRem
dayjs.extend(utc)

type HasConvictions = {
  driving: boolean | null | undefined
  criminal: boolean | null | undefined
}

export const DriverConvictions = () => {
  const quote = useQuote()
  const { showErrorBanner, notAnsweredMessage, updateValidInputs, validInputs } = usePreQuoteContext()
  const {
    state: { showModal, activeModal, selectedDriverIndex },
    handleShowModal,
  } = useContext(ProviderContext)
  const {
    updatedDriverDetails,
    setUpdatedDriverDetails,
    updatedAdditionalDriver,
    setUpdatedAdditionalDriver,
    validateAndSave,
  } = useDriverPageContext()

  const additionalDrivers = quote?.details?.additional_drivers || []
  const selectedDriver = !selectedDriverIndex ? quote?.details?.proposer : additionalDrivers[selectedDriverIndex - 1]

  const [hasConvictions, setHasConvictions] = useState<HasConvictions>({
    driving: null,
    criminal: null,
  })
  const [convictions, setConvictions] = useState<any[]>([])

  const [convictionNumber, setConvictionNumber] = useState<number | undefined>()
  const [isEdit, setIsEdit] = useState<boolean>(false)

  useEffect(() => {
    setHasConvictions({
      driving: selectedDriver?.has_driving_convictions,
      criminal: selectedDriver?.has_criminal_conviction,
    })
    setConvictions(
      selectedDriver?.convictions?.map(conviction => {
        return omitObjectKey('__typename', conviction)
      }) || [],
    )
  }, [selectedDriverIndex])

  const drivingConvictionsError =
    showErrorBanner && !validInputs[selectedDriverIndex]?.hasDrivingConvictions ? notAnsweredMessage : ''
  const criminalConvictionsError =
    showErrorBanner && !validInputs[selectedDriverIndex]?.hasCriminalConviction ? notAnsweredMessage : ''

  const handleConvictionsDataLayerEvent = (value: 'driving' | 'criminal') => {
    DataLayer.customEvent({
      eventName: `${value} convictions ${value ? 'yes' : 'no'}`,
      section: 'driver',
      eventType: 'form tracking',
    })
  }

  const handleDrivingConvictions = (value: boolean | null) => {
    setConvictionNumber(1)
    setHasConvictions({ ...hasConvictions, driving: value })
    handleConvictionsDataLayerEvent('driving')
    const convictionsUpdate = value ? {} : { convictions: [] }

    if (!selectedDriverIndex) {
      setUpdatedDriverDetails({ ...updatedDriverDetails, hasDrivingConvictions: value, ...convictionsUpdate })
    } else {
      setUpdatedAdditionalDriver({ ...updatedAdditionalDriver, has_driving_convictions: value, ...convictionsUpdate })
    }

    value ? handleShowModal('add_conviction') : setConvictions([])
  }

  const handleCriminalConvictions = (value: boolean | null) => {
    setHasConvictions({ ...hasConvictions, criminal: value })
    handleConvictionsDataLayerEvent('criminal')
    const primaryDriver = !selectedDriverIndex

    if (primaryDriver) {
      setUpdatedDriverDetails({ ...updatedDriverDetails, hasCriminalConviction: value })
    } else {
      setUpdatedAdditionalDriver({ ...updatedAdditionalDriver, has_criminal_conviction: value })
    }
  }

  const handleConvictionEdit = (index: number) => {
    setConvictionNumber(index + 1)
    setIsEdit(true)
    handleShowModal('add_conviction')
  }

  const handleRemove = (number: number) => {
    const convictionsArray = convictions
    convictionsArray.splice(number, 1)
    if (convictions.length < 1) document.getElementById('input-has-motoring-convictions-toggle-no')?.click()
    setUpdatedDriverDetails({
      ...updatedDriverDetails,
      convictions: [...convictionsArray],
      hasDrivingConvictions: convictionsArray?.length > 0,
    })
    setConvictions([...convictionsArray])
  }

  useEffect(() => {
    updateValidInputs({
      field: 'hasDrivingConvictions',
      value: typeof hasConvictions.driving === 'boolean',
      index: selectedDriverIndex,
    })
  }, [hasConvictions.driving, selectedDriverIndex])

  useEffect(() => {
    updateValidInputs({
      field: 'hasCriminalConviction',
      value: typeof hasConvictions.criminal === 'boolean',
      index: selectedDriverIndex,
    })
  }, [hasConvictions.criminal, selectedDriverIndex])

  return (
    <>
      <GenericSectionStyle>
        <H4 className="title">{`${YourTheir()} convictions.`}</H4>
        <div className="generic-wrapper convictions">
          <div className="question-container" id="hasDrivingConvictions">
            <div className="question-text-container">
              <H5>
                {`In the last 5 years, have ${YouThey()} received any motoring convictions, licence endorsements or fixed
                penalties? Or have ${YouThey()} been disqualified from driving?`}
              </H5>
              <ParagraphBody className="question-info">This includes any pending prosecutions.</ParagraphBody>
            </div>
            <AccessibleFormWrapper className="input-info-container" onSubmit={validateAndSave}>
              <SegmentedControl
                options={[
                  { label: 'yes', value: true },
                  { label: 'no', value: false },
                ]}
                name="has-motoring-convictions"
                preFilledValue={hasConvictions.driving ?? undefined}
                error={drivingConvictionsError}
                onClick={value => handleDrivingConvictions(value)}
                dataCy="has-motoring-convictions-toggle"
                tabFocusTrap={showModal ? -1 : 0}
                ariaLiveOnFocus
              />
              <ToolTip
                text={`Enter the details of any driving offences ${YouveTheyve()} committed, including Fixed Penalty Notices such as speeding. We don't need to know about Penalty Charge Notices such as parking offences, so don't include these.`}
                className="tool-tip tip-top-medium"
                tabFocusTrap={showModal ? -1 : 0}
              />
            </AccessibleFormWrapper>
          </div>
          {convictions && convictions?.length !== 0 && (
            <>
              <div className="additonal-wrapper">
                {convictions.map((conviction: any, i: number) => {
                  return (
                    <div className="attribute-box">
                      <AttributeBox
                        title={`Conviction ${i + 1}`}
                        attributes={[
                          { label: 'Details', value: conviction.code },
                          { label: 'Date', value: dayjs(conviction?.date).format('DD/MM/YYYY') },
                          { label: 'Points received', value: conviction.points },
                          { label: 'Months banned', value: conviction.ban },
                        ]}
                        onEdit={() => handleConvictionEdit(i)}
                        onRemove={() => handleRemove(i)}
                        tabFocusTrap={showModal ? -1 : 0}
                      />
                    </div>
                  )
                })}
              </div>
              <div className="additional-button">
                <ButtonGeneral
                  label="Add another conviction"
                  onClick={() => handleShowModal('add_conviction')}
                  dataCy="add-extra-claim-button"
                  icon="IconPlusSmall"
                  tabFocusTrap={showModal ? -1 : 0}
                />
              </div>
            </>
          )}
          <div className="question-container" id="hasCriminalConviction">
            <div className="question-text-container">
              <H5>{`Do ${YouThey()} have any unspent non-motoring criminal convictions?`}</H5>
            </div>
            <AccessibleFormWrapper className="input-info-container" onSubmit={validateAndSave}>
              <SegmentedControl
                options={[
                  { label: 'yes', value: true },
                  { label: 'no', value: false },
                ]}
                name="has-criminal-convictions"
                preFilledValue={hasConvictions.criminal ?? undefined}
                error={criminalConvictionsError}
                onClick={value => handleCriminalConvictions(value)}
                dataCy="has-criminal-convictions-toggle"
                tabFocusTrap={showModal ? -1 : 0}
                ariaLiveOnFocus
              />
              <ToolTip
                text={`If ${YouThey()} have any unspent convictions, it’s really important that you tell us. It might invalidate your insurance if you don't.`}
                className="tool-tip tip-top-medium"
                tabFocusTrap={showModal ? -1 : 0}
              />
            </AccessibleFormWrapper>
          </div>
        </div>
      </GenericSectionStyle>

      {showModal && activeModal === 'add_conviction' && (
        <AddConvictionsModal
          convictions={convictions}
          setConvictions={setConvictions}
          updatedDriverDetails={updatedDriverDetails}
          setUpdatedDriverDetails={setUpdatedDriverDetails}
          updatedAdditionalDriver={updatedAdditionalDriver}
          setUpdatedAdditionalDriver={setUpdatedAdditionalDriver}
          isEdit={isEdit}
          setIsEdit={setIsEdit}
          convictionNumber={convictionNumber}
          notAnsweredMessage={notAnsweredMessage}
        />
      )}
    </>
  )
}
